import { axios } from "../connection/ConnectionHandler";
import { API } from "../constants/api";
import { keyBy } from "lodash";
import {
    ADD_NEW_TICKET,
    DELETE_TICKET,
    TICKET_FORM_TOOGLE_LOADING,
    SET_TICKET_LIST,
    SHOW_NOTIFICATION,
    UPDATE_TICKET,
} from "../constants";
import { formatTickets } from "./settingsActionsUtils";

/* TICKET LIST */
export const fetchTickets = () => {
    return async (dispatch) => {
        const response = await axios
            .get(API.tickets.list)
            .then((response) => {
                const tickets = formatTickets(response.data);
                dispatch({
                    type: SET_TICKET_LIST,
                    payload: keyBy(tickets, "_id"),
                });
                return response.status;
            })
            .catch((err) => err);
        return response;
    };
};

export const fetchTicketsByDateRange = (startDate, endDate) => {
    return async (dispatch) => {
        dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
        const response = await axios
            .get(API.tickets.getByDateRange, {
                params: {
                    startDate: startDate.format('YYYY-MM-DD'),
                    endDate: endDate.format('YYYY-MM-DD')
                }
            })
            .then((response) => {
                const tickets = formatTickets(response.data);
                dispatch({
                    type: SET_TICKET_LIST,
                    payload: keyBy(tickets, "_id"),
                });
                dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
                return response.status;
            })
            .catch((err) => {
                dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
                return err;
            });
        return response;
    };
};

export const generateTicketsPDF = (startDate, endDate) => {
    return async (dispatch) => {
        dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
        const response = await axios
            .get(API.tickets.generatePDF, {
                params: {
                    startDate: startDate.format('YYYY-MM-DD'),
                    endDate: endDate.format('YYYY-MM-DD')
                },
                responseType: 'blob'
            })
            .then((response) => {
                dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
                // Crear un blob con el PDF
                const blob = new Blob([response.data], { type: 'application/pdf' });
                // Crear una URL para el blob
                const url = window.URL.createObjectURL(blob);
                // Crear un enlace temporal y hacer clic en él para descargar
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `tickets_${startDate.format('YYYY-MM-DD')}_${endDate.format('YYYY-MM-DD')}.pdf`);
                document.body.appendChild(link);
                link.click();
                // Limpiar
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
                return response.status;
            })
            .catch((err) => {
                dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
                return err;
            });
        return response;
    };
};

export const fetchTicketsForExcel = (startDate = null, endDate = null) => {
    return async (dispatch) => {
        dispatch({ type: TICKET_FORM_TOOGLE_LOADING });

        const hasDateRange = startDate && endDate;
        const response = await axios
            .get(hasDateRange ? API.tickets.getByDateRange : API.tickets.list, hasDateRange ? {
                params: {
                    startDate: startDate.format('YYYY-MM-DD'),
                    endDate: endDate.format('YYYY-MM-DD')
                }
            } : undefined)
            .then((response) => {
                dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
                return {
                    status: response.status,
                    data: formatTickets(response.data),
                };
            })
            .catch((err) => {
                dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
                return err;
            });
        return response;
    };
};

/* INFORME DE EFICIENCIA */
export const fetchTicketsReport = (filters = {}) => {
    return async (dispatch) => {
        const { startDate, endDate, applications, levels, assignedTo } = filters;
        const params = {};

        if (startDate) {
            params.startDate = startDate.format("YYYY-MM-DD");
        }
        if (endDate) {
            params.endDate = endDate.format("YYYY-MM-DD");
        }
        if (applications && applications.length) {
            params.applications = applications.join(",");
        }
        if (levels && levels.length) {
            params.levels = levels.join(",");
        }
        if (assignedTo && assignedTo.length) {
            params.assignedTo = assignedTo.join(",");
        }

        const response = await axios
            .get(API.tickets.report, { params })
            .then((response) => ({
                status: response.status,
                data: response.data,
            }))
            .catch((err) => {
                dispatch({
                    type: SHOW_NOTIFICATION,
                    payload: {
                        show: true,
                        status: "error",
                        message: "No se pudo generar el informe de tickets.",
                    },
                });
                return err;
            });
        return response;
    };
};

// TICKETS
export const createTicket = (newTicket) => {
    return async (dispatch) => {
        dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
        const response = await axios
            .post(API.tickets.create, newTicket)
            .then((response) => {
                const ticket = formatTickets(response.data);
                dispatch({ type: ADD_NEW_TICKET, payload: ticket });
                dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
                dispatch({
                    type: SHOW_NOTIFICATION,
                    payload: {
                        show: true,
                        status: "success",
                        message: "Ticket creado con éxito.",
                    },
                });
                return response && response.status;
            })
            .catch((err) => {
                dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
                return err;
            });
        return response;
    };
};

export const updateTicket = (updatedTicket, skipNotification = false) => {
    return async (dispatch, getState) => {
        dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
        
        // Obtener el ticket original desde el estado actual
        const currentState = getState();
        
        // Buscar el ticket por su ID
        const originalTicket = currentState.tickets ? currentState.tickets[updatedTicket._id] : null;
        
        // Crear un mensaje dinámico comparando campos
        let updateMessage = "Ticket actualizado";
        let updatedFields = [];
        
        if (originalTicket) {
            // Comparar solo si tenemos el ticket original
            if (updatedTicket.title && updatedTicket.title !== originalTicket.title) 
                updatedFields.push("título");
            if (updatedTicket.state && updatedTicket.state !== originalTicket.state) 
                updatedFields.push("estado");
            if (updatedTicket.priority && updatedTicket.priority !== originalTicket.priority) 
                updatedFields.push("prioridad");
            if (updatedTicket.bodyBusiness && updatedTicket.bodyBusiness !== originalTicket.bodyBusiness) 
                updatedFields.push("contenido del cliente");
            if (updatedTicket.bodyDevelop && updatedTicket.bodyDevelop !== originalTicket.bodyDevelop) 
                updatedFields.push("contenido del desarrollador");
            
            // Verificar cambios en la asignación de usuario
            const hasAssignedToChanged = () => {
                // Normalizar los valores para comparar solo los IDs
                const originalAssignedId = originalTicket.assignedTo ? 
                    (typeof originalTicket.assignedTo === 'object' ? originalTicket.assignedTo._id : originalTicket.assignedTo) 
                    : null;
                const updatedAssignedId = updatedTicket.assignedTo ? 
                    (typeof updatedTicket.assignedTo === 'object' ? updatedTicket.assignedTo._id : updatedTicket.assignedTo) 
                    : null;
                
                return originalAssignedId !== updatedAssignedId;
            };
            
            if (hasAssignedToChanged()) {
                if (updatedTicket.assignedTo && !originalTicket.assignedTo) {
                    updatedFields.push("asignación de usuario");
                } else if (!updatedTicket.assignedTo && originalTicket.assignedTo) {
                    updatedFields.push("eliminación de asignación");
                } else {
                    updatedFields.push("cambio de asignación");
                }
            }
                
            // Comparación especial para fechas (ignorando diferencias en formato)
            const hasExpectedDateChanged = () => {
                // Si uno tiene fecha y el otro no, hay cambio
                if ((!updatedTicket.expectedDate && originalTicket.expectedDate) || 
                    (updatedTicket.expectedDate && !originalTicket.expectedDate)) {
                    return true;
                }
                
                // Si ambos tienen fecha, comparar valores normalizados (solo la fecha, no la hora ni formato)
                if (updatedTicket.expectedDate && originalTicket.expectedDate) {
                    const updatedDate = updatedTicket.expectedDate.split('T')[0];
                    const originalDate = originalTicket.expectedDate.split('T')[0];
                    return updatedDate !== originalDate;
                }
                
                return false;
            };
            
            if (hasExpectedDateChanged()) {
                updatedFields.push("fecha prevista");
            }

            if (
                Object.prototype.hasOwnProperty.call(updatedTicket, "expectedHours") &&
                updatedTicket.expectedHours !== originalTicket.expectedHours
            ) {
                updatedFields.push("horas previstas");
            }
            
            if (updatedTicket.urgent !== undefined && updatedTicket.urgent !== originalTicket.urgent)
                updatedFields.push(updatedTicket.urgent ? "marcado como urgente" : "desmarcado como urgente");
            if (updatedTicket.order && updatedTicket.order !== originalTicket.order) 
                updatedFields.push("orden");
            
            // Comparación de documentos
            const documentsChanged = () => {
                const origDocs = originalTicket.documents || [];
                const newDocs = updatedTicket.documents || [];
                
                if (origDocs.length !== newDocs.length) return true;
                
                // Comparar por ID o UID
                const origIds = origDocs.map(d => d.uid || d._id).sort();
                const newIds = newDocs.map(d => d.uid || d._id).sort();
                
                return JSON.stringify(origIds) !== JSON.stringify(newIds);
            };
            
            if (documentsChanged()) {
                updatedFields.push("documentos");
            }
        } else {
            // Si no podemos encontrar el ticket original, simplemente listamos los campos que tienen valores significativos
            if (updatedTicket.title) updatedFields.push("título");
            if (updatedTicket.state) updatedFields.push("estado");
            if (updatedTicket.priority) updatedFields.push("prioridad");
            if (updatedTicket.bodyBusiness && updatedTicket.bodyBusiness.trim()) updatedFields.push("contenido del cliente");
            if (updatedTicket.bodyDevelop && updatedTicket.bodyDevelop.trim()) updatedFields.push("contenido del desarrollador");
            if (updatedTicket.expectedDate) updatedFields.push("fecha prevista");
            if (updatedTicket.expectedHours !== undefined && updatedTicket.expectedHours !== null) updatedFields.push("horas previstas");
            if (updatedTicket.assignedTo) updatedFields.push("asignación de usuario");
            if (updatedTicket.urgent) updatedFields.push("marcado como urgente");
            if (updatedTicket.order) updatedFields.push("orden");
            if (updatedTicket.documents && updatedTicket.documents.length > 0) updatedFields.push("documentos");
        }
        
        if (updatedFields.length > 0) {
            updateMessage += ": " + updatedFields.join(", ");
        }
        
        // Agregar skipNotification al payload si es necesario
        const payload = { ...updatedTicket };
        if (skipNotification) {
            payload.skipNotification = true;
        }
        
        const response = await axios
            .put(`${API.tickets.edit}${updatedTicket._id}`, payload)
            .then((response) => {
                const ticket = formatTickets(response.data);
                dispatch({ type: UPDATE_TICKET, payload: ticket });
                dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
                
                // Solo mostrar notificación si no se está saltando las notificaciones o si es un cambio importante
                if (!skipNotification) {
                    dispatch({
                        type: SHOW_NOTIFICATION,
                        payload: {
                            show: true,
                            status: "info",
                            message: updateMessage,
                        },
                    });
                }
                return response.status;
            })
            .catch((err) => {
                dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
                return err;
            });
        return response;
    };
};

export const deleteTickets = (Ids) => {
    return async (dispatch) => {
        dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
        const response = await axios
            .delete(`${API.tickets.delete}`, { data: Ids })
            .then((response) => {
                dispatch({ type: DELETE_TICKET, payload: Ids });
                dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
                dispatch({
                    type: SHOW_NOTIFICATION,
                    payload: {
                        show: true,
                        status: "info",
                        message: "Ticket eliminado.",
                    },
                });
                return response && response.status;
            })
            .catch((err) => {
                dispatch({ type: TICKET_FORM_TOOGLE_LOADING });
                return err;
            });
        return response;
    };
};
